/**
 * Klartid — felkoder. En kod, en HTTP-status, ett besked om vad klienten
 * ska gora, och den svenska text anvandaren faktiskt far se pa telefonen.
 * Filen importerar ingenting; den ar botten i beroendekedjan.
 *
 * retry — OCH EXAKT VAD KLIENTEN GOR
 *   'retry'    Tillfalligt. Operationen LIGGER KVAR i utkorgen och skickas
 *              om automatiskt med vaxande fordrojning (2 s, 4 s, 8 s ...
 *              upp till 15 minuter). Anvandaren gor ingenting.
 *   'hold'     Hindret ligger UTANFOR operationen: utloggad, obetald
 *              prenumeration, for stort kuvert. Operationen LIGGER KVAR
 *              med sitt op_id och skickas om OFORANDRAD sa fort hindret ar
 *              undanrojt — efter inloggning, efter betalning, eller i
 *              mindre kuvert. Inget hamnar i "Behover atgard".
 *   'give_up'  Nagot i sjalva operationen ar fel; att skicka om samma sak
 *              hjalper inte. Operationen FLYTTAS UT UR UTKORGEN till
 *              listan "Behover atgard", med sitt innehall och sin felkod
 *              kvar lokalt, och skickas om forst nar anvandaren oppnat,
 *              rattat och sparat om — vilket ger ett NYTT op_id. Ingenting
 *              kastas nagonsin bort automatiskt: en montor som sparat atta
 *              timmar och fatt PRICE_MISSING eller WEEK_LOCKED hittar dem
 *              i "Behover atgard", inte i papperskorgen.
 *
 * HELANROPSFEL ROR ALDRIG UTKORGEN. retry-faltet beskriver vad som hander
 * med EN OPERATION, dvs. ett fel som bars i SyncOpResult.error. Faller
 * HELA SYNC_PUSH-anropet (UNAUTHENTICATED, LICENSE_EXPIRED, FORBIDDEN,
 * SUBSCRIPTION_LOCKED, TOO_MANY_OPS, VALIDATION_FAILED pa kuvertet,
 * RATE_LIMITED, SERVER_ERROR, SERVICE_UNAVAILABLE) ligger VARJE operation
 * kvar med sitt op_id, oavsett kodens retry-varde; vid TOO_MANY_OPS
 * halverar klienten kuvertet och skickar om. Det ar darfor de koder som
 * per definition ar helanropsfel och samtidigt lovar i sin text att
 * utkorgen ligger kvar ar markta 'hold' och inte 'give_up' — en enda
 * utgangen token far aldrig tomma en montors utkorg till en atgardslista.
 *
 * FORBIDDEN AR DEN ENDA KODEN SOM SPELAR BADA ROLLERNA, och undantaget
 * skrivs ut har sa att ingen klient behover gissa det: som HELANROPSFEL
 * (saknat medlemskap, bevis for fel firma, degraderad chef) galler stycket
 * ovan — varje operation ligger kvar orord och retry-faltet lases INTE. Som
 * OPERATIONSFEL (rollen far inte gora just den saken, eller ett op_id som
 * tillhor ett annat konto eller en annan enhet) galler retry 'give_up': just
 * den operationen flyttas till "Behover atgard". Darfor ar FORBIDDEN markt
 * 'give_up' trots att den ocksa ar ett helanropsfel; en klient som laser
 * retry-faltet vid ett 403 pa HELA anropet tommer en utkorg som skulle ha
 * legat kvar.
 *
 * http_status galler nar HELA anropet faller. I ett i ovrigt giltigt
 * SYNC_PUSH-kuvert svarar servern alltid 200, och de operationer som foll
 * bar sitt fel i SyncOpResult.error — dar anvands bara code, message_sv
 * och detail.
 *
 * DATABASTRIGGRARNA i schema/d1.sql och schema/company.sql anvander
 * ORDAGRANT namnen nedan ('GUEST_ORG_NUMBER_INVALID',
 * 'OWNER_MUST_BE_MANAGER', 'VALIDATION_FAILED'), sa att servern kan mappa
 * ett triggerfel rakt till en ErrorSpec utan att tolka text.
 *
 * UNIKHETSBROTT MAPPAS TILL ROW_DUPLICATE, aldrig till SERVER_ERROR. Varje
 * UNIQUE-index i schemana (absence_one_per_day, price_list_one_default,
 * price_rate_key, work_order_guest_key, work_order_photo.object_key,
 * expense.receipt_object_key) kan brytas av en HELT NORMAL offlineutkorg:
 * tva telefoner, eller samma telefon tva ganger med olika op_id. Som
 * SERVER_ERROR (retry 'retry') skulle en sadan operation ligga kvar i
 * utkorgen och skickas om med vaxande fordrojning i evighet for nagot som
 * aldrig kan lyckas — exakt det utfall den har filen finns for att gora
 * omojligt.
 *
 * TEXTERNA ar skrivna for den arbetande chefen pa en telefon i ett
 * trapphus: vad som hande och vad han gor at det. De visas ordagrant.
 */

export type RetryPolicy = 'give_up' | 'retry' | 'hold';

export type ErrorCode =
  | 'VALIDATION_FAILED'
  | 'UNAUTHENTICATED'
  | 'LICENSE_EXPIRED'
  | 'FORBIDDEN'
  | 'OWNER_REQUIRED'
  | 'OWNER_MUST_BE_MANAGER'
  | 'SUBSCRIPTION_LOCKED'
  | 'WEEK_LOCKED'
  | 'WEEK_TRANSITION_INVALID'
  | 'ROW_INVOICED'
  | 'ROW_IN_USE'
  | 'ROW_DUPLICATE'
  | 'SYNC_CONFLICT'
  | 'DUPLICATE_OP'
  | 'GUEST_ORG_NUMBER_MISSING'
  | 'GUEST_ORG_NUMBER_INVALID'
  | 'UNKNOWN_WORK_ORDER'
  | 'UNKNOWN_REFERENCE'
  | 'PRICE_MISSING'
  | 'ORG_NUMBER_TAKEN'
  | 'FILE_TOO_LARGE'
  | 'TOO_MANY_OPS'
  | 'RATE_LIMITED'
  | 'SERVER_ERROR'
  | 'SERVICE_UNAVAILABLE';

export interface ErrorSpec {
  readonly code: ErrorCode;
  readonly http_status: number;
  readonly retry: RetryPolicy;
  readonly message_sv: string;
}

/**
 * Extra fakta for klientens logik — ALDRIG fritext till anvandaren.
 *   SYNC_CONFLICT          { server_version, server_seq }  BADA ar null nar
 *                          raden inte finns alls (base_version ar ett TAL
 *                          mot ett okant id). Da finns ingen serverversion
 *                          att rapportera, och 0/0 vore en logn: bade
 *                          CHECK (version > 0) / CHECK (seq > 0) i schemat
 *                          och de markta Version/Seq borjar pa 1.
 *   WEEK_LOCKED            { iso_week, state }  state ar det VERKLIGA
 *                          tillstandet, 'approved' eller 'locked'; vid en
 *                          FLYTT mellan tva veckor ar iso_week den vecka
 *                          som stoppade skrivningen (den gamla provas
 *                          forst, se schema/company.sql)
 *   WEEK_TRANSITION_INVALID { iso_week, state }  state ar veckans VERKLIGA
 *                          tillstand nar overgangen provades, sa att
 *                          klienten kan visa vad som galler i stallet for
 *                          att be nagon kontrollera falten
 *   ROW_INVOICED           { table, id }  invoice_run_id skickas ALDRIG med —
 *                          pekaren ar fakturadata och far inte na en klient i
 *                          montors- eller gastlage (beslut 16 och 17; se
 *                          TimeEntryWorkerRow, dar den ar bortskalad av exakt
 *                          det skalet). Klienten behover bara veta ATT raden
 *                          ar fakturerad, och det sager message_sv; chefen ser
 *                          korningen i fakturavyn. Samma losning som
 *                          PRICE_MISSING nedan, av samma skal. FORMEN AR
 *                          TYPAD, se RowInvoicedDetail — regeln ar inte en
 *                          artighet utan nagot tsc haller, precis som Omit
 *                          haller den i TimeEntryWorkerRow
 *   ROW_IN_USE             { table, id, blocked_by }  blocked_by =
 *                          tabellnamnet som fortfarande pekar pa raden.
 *                          DELETE_COMPANY anvander samma form och samma kod
 *                          for en firma som fortfarande betalar:
 *                          { table: 'company', id, blocked_by:
 *                          'subscription' }. Ingen egen kod behovs — det ar
 *                          ordagrant vad koden betyder, och subscription
 *                          pekar pa company med en frammande nyckel. Klienten
 *                          kanner igen blocked_by och skickar agaren till
 *                          BILLING_PORTAL for att saga upp forst
 *   ROW_DUPLICATE          { table, id, conflict }  conflict = NAMNET pa
 *                          det UNIQUE-index som brots, t.ex.
 *                          'absence_one_per_day' eller
 *                          'price_list_one_default', sa att klienten kan
 *                          visa ratt sak att oppna i "Behover atgard".
 *                          SQLite namnger KOLUMNERNA i sitt felmeddelande
 *                          ('UNIQUE constraint failed: absence.account_id,
 *                          absence.work_date'), sa servern oversatter
 *                          kolumnlistan till indexnamnet ur schemat — en
 *                          fast tabell, aldrig radtexten ut till klienten
 *   PRICE_MISSING          { role }  price_list_id skickas ALDRIG med —
 *                          pekaren ar prisdata och far inte na en klient i
 *                          montors- eller gastlage (beslut 16). Chefen ser
 *                          vilken lista som saknar rad i prislistevyn. Ocksa
 *                          TYPAD, se PriceMissingDetail.
 *   UNKNOWN_WORK_ORDER     { id }
 *   UNKNOWN_REFERENCE      { table, id }  table = 'customer', 'task',
 *                          'price_list', 'member' eller 'file'
 *   DUPLICATE_OP           { op_id }
 *   VALIDATION_FAILED      { field }  ALLTID exakt den formen, aldrig nagon
 *                          annan: koden betyder att ett NAMNGIVET FALT i
 *                          begaran ar felskrivet, och field ar dess namn —
 *                          t.ex. 'name', 'object_key', 'to_date',
 *                          'work_date', 'period_start', 'account_id',
 *                          'confirm_org_number', 'ops[].op_id'. Det gar aven
 *                          for ett falt som ar RATT SKRIVET men FEL VARDE nar
 *                          faltets hela uppgift ar att stamma: DELETE_COMPANY:s
 *                          bekraftelse ar exakt det, och da finns ett falt att
 *                          peka pa och texten "kontrollera falten" ar sann.
 *                          Ett LAGESFEL i en i ovrigt
 *                          giltig begaran ar aldrig VALIDATION_FAILED: da
 *                          finns inget falt att peka pa och texten
 *                          ("kontrollera falten") skulle be anvandaren om
 *                          nagot som inte hjalper. Veckans tillstandsmaskin
 *                          har darfor en egen kod, WEEK_TRANSITION_INVALID
 *   TOO_MANY_OPS           { max }
 *   FILE_TOO_LARGE         { max_bytes }
 *   RATE_LIMITED           { retry_after_ms }
 *   OWNER_MUST_BE_MANAGER  { account_id }
 *   Ovriga koder har ingen detail.
 *
 * TVA AV FORMERNA AR TYPADE OCH INTE BARA BESKRIVNA. ROW_INVOICED och
 * PRICE_MISSING ar de tva dar prosan ovan ar NORMATIV — de namnger ett falt
 * som ALDRIG far folja med (invoice_run_id respektive price_list_id) — och en
 * oppen Record hade latit precis det faltet laggas i detail utan att tsc sa
 * ett ord. Med RowInvoicedDetail och PriceMissingDetail nedan fangas det, pa
 * samma satt som Omit fangar det i TimeEntryWorkerRow. Ovriga koder behaller
 * den oppna formen: deras detaljer ar rena upplysningar utan sekretessgrans.
 */
export type ErrorDetailValue = string | number | boolean | null;

/** Formen for de koder som inte har en egen, namngiven detail nedan. */
export type ErrorDetail = Readonly<Record<string, ErrorDetailValue>>;

/**
 * ROW_INVOICED — { table, id } och INGENTING MER. invoice_run_id ar
 * fakturadata och far aldrig na montors- eller gastlage (beslut 16 och 17);
 * eftersom typen ar sluten faller ett forsok att skicka med den i tsc.
 */
export interface RowInvoicedDetail {
  readonly table: string;
  readonly id: string;
}

/**
 * PRICE_MISSING — { role } och INGENTING MER. price_list_id ar prisdata och
 * far aldrig na montors- eller gastlage (beslut 16).
 * ROLLERNA SKRIVS UT SOM LITERALUNION i stallet for att importeras som
 * PriceRole: den har filen importerar ingenting (den ar botten i
 * beroendekedjan, och shared/types.ts importerar ApiErrorBody harifran — en
 * import at andra hallet vore en cykel). Att de tva inte kan divergera
 * bevisas i shared/types.ts, dar PriceMissingRoleIsPriceRole faller om
 * mangderna skiljer sig.
 */
export interface PriceMissingDetail {
  readonly role: 'manager' | 'worker' | 'apprentice' | 'guest';
}

/** Detaljens form for EN kod. Okanda koder faller tillbaka pa ErrorDetail. */
export type ErrorDetailFor<TCode extends ErrorCode> =
  TCode extends 'ROW_INVOICED'
    ? RowInvoicedDetail
    : TCode extends 'PRICE_MISSING'
      ? PriceMissingDetail
      : ErrorDetail;

/** Felkroppen for EN kod, med den detail just den koden far bara. */
export interface ApiErrorBodyFor<TCode extends ErrorCode> {
  code: TCode;
  message_sv: string;
  detail?: ErrorDetailFor<TCode>;
}

/**
 * Felkroppen som en DISKRIMINERAD UNION pa code, sa att detail alltid provas
 * mot RATT form. Som en enkel interface med `detail?: ErrorDetail` gick
 * regeln ovan inte att halla — alla objekt passar en oppen Record.
 * KONSEKVENS FOR SERVERN: en union gar inte att satta ihop ur en GENERISK
 * `code: ErrorCode`. Felkroppar byggs darfor genom en hjalpare som ar typad
 * per kod, ApiErrorBodyFor<'ROW_INVOICED'> och sa vidare — inte genom att
 * lagga ihop ett objekt med en kod som forst ar kand vid korning. Det ar
 * priset for att beslut 16:s pekare inte kan slinka med i detail, och det ar
 * medvetet betalt.
 */
export type ApiErrorBody = {
  [TCode in ErrorCode]: ApiErrorBodyFor<TCode>;
}[ErrorCode];

export const ERRORS = {
  VALIDATION_FAILED: {
    code: 'VALIDATION_FAILED',
    http_status: 400,
    retry: 'give_up',
    message_sv: 'Något i uppgifterna stämmer inte. Kontrollera fälten och spara igen.',
  },

  UNAUTHENTICATED: {
    code: 'UNAUTHENTICATED',
    http_status: 401,
    retry: 'hold',
    message_sv: 'Du är utloggad. Logga in med koden vi mejlar dig.',
  },

  /**
   * Beslut 12: beviset galler 30 dagar och fornyas tyst vid varje synk.
   * Har telefonen varit utan nat langre maste den online en gang. Inget ar
   * forlorat — utkorgen ligger kvar och skickas oforandrad ('hold').
   */
  LICENSE_EXPIRED: {
    code: 'LICENSE_EXPIRED',
    http_status: 401,
    retry: 'hold',
    message_sv: 'Appen har varit utan kontakt i mer än 30 dagar. Logga in igen så skickas allt du sparat under tiden.',
  },

  /**
   * Rollen las ur D1, aldrig ur licensbeviset: en avskedad montor, en
   * degraderad chef eller en borttagen bokforare far det har direkt. Per
   * operation betyder det att rollen inte far gora just den saken.
   * retry 'give_up' galler BARA operationsfallet (se HELANROPSFEL overst):
   * faller HELA anropet pa FORBIDDEN ligger utkorgen kvar orord och det har
   * faltet lases inte.
   */
  FORBIDDEN: {
    code: 'FORBIDDEN',
    http_status: 403,
    retry: 'give_up',
    message_sv: 'Du har inte behörighet till det här.',
  },

  /**
   * VAR DEN FYRAS: pa de rutter som bar requires_owner: true i
   * shared/routes.ts — TRANSFER_OWNERSHIP, BILLING_CHECKOUT, BILLING_PORTAL
   * och DELETE_COMPANY. Anroparen ar manager (annars hade roles-kontrollen redan
   * gett FORBIDDEN) men inte company.owner_account_id.
   * SKILD FRAN FORBIDDEN med flit: rollen racker till, det ar AGARSKAPET som
   * saknas, och en chef som far "Du har inte behorighet till det har" nar
   * saken egentligen ar "be agaren gora det" letar pa fel stalle. Skild fran
   * OWNER_MUST_BE_MANAGER ocksa: den koden handlar om MOTTAGAREN vid en
   * overlatelse, den har om ANROPAREN.
   */
  OWNER_REQUIRED: {
    code: 'OWNER_REQUIRED',
    http_status: 403,
    retry: 'give_up',
    message_sv: 'Bara den som äger firman i Klartid kan göra det här.',
  },

  OWNER_MUST_BE_MANAGER: {
    code: 'OWNER_MUST_BE_MANAGER',
    http_status: 409,
    retry: 'give_up',
    message_sv: 'Firman kan bara överlåtas till någon som redan är chef i den, och den som äger firman måste vara chef. Gör personen till chef först.',
  },

  SUBSCRIPTION_LOCKED: {
    code: 'SUBSCRIPTION_LOCKED',
    http_status: 403,
    retry: 'hold',
    message_sv: 'Prenumerationen är pausad. Du kan läsa och exportera allt, men inget nytt kan sparas förrän betalningen är ordnad.',
  },

  /**
   * Fyras i TVA lagen, och texten maste vara sann i bada: veckan star i
   * 'approved' (attesterad, men INTE last) eller i 'locked'. detail.state
   * bar det verkliga tillstandet — texten far darfor inte pasta "last", och
   * den far inte heller be om nagot annat an det som faktiskt behovs, for
   * det har ar det enda montoren ser pa telefonen. Vagen tillbaka ar
   * densamma i bada lagena: chefen aterppnar veckan (beslut 19).
   * Undantaget som INTE ger det har felet ar chefens EGEN vecka i
   * 'approved' (MANAGER_OWN_WEEK_WRITABLE_STATES, beslut 10).
   */
  WEEK_LOCKED: {
    code: 'WEEK_LOCKED',
    http_status: 409,
    retry: 'give_up',
    message_sv: 'Veckan är attesterad och kan inte ändras. Be chefen öppna den igen om något ska rättas.',
  },

  /**
   * Veckans tillstandsmaskin (WEEK_TRANSITIONS i shared/routes.ts,
   * week_status i schema/company.sql) tillater inte overgangen fran det lage
   * veckan FAKTISKT star i: 'lock' pa en vecka som inte ar attesterad,
   * 'reopen' pa en som aldrig blivit det, 'submit' eller 'approve' pa en som
   * redan ar attesterad eller last. Begaran ar valformad — det ar veckan
   * som flyttat sig, oftast for att en annan chef hunnit fore — sa detta ar INTE
   * VALIDATION_FAILED { field }: det finns inget falt att ratta, och den
   * kodens text skulle be montoren kontrollera falten i onodan. Egen kod
   * med samma detail som WEEK_LOCKED, sa att klienten kan visa vilken vecka
   * det galler och vad den star i nu.
   * 'give_up': samma begaran kan aldrig lyckas oforandrad. Anvandaren
   * hittar den i "Behover atgard", ser veckans verkliga lage och gor om.
   */
  WEEK_TRANSITION_INVALID: {
    code: 'WEEK_TRANSITION_INVALID',
    http_status: 409,
    retry: 'give_up',
    message_sv: 'Veckan står i ett annat läge än du såg — någon annan hann före. Hämta in det senaste och gör om det du ville.',
  },

  /**
   * Raden bar en invoice_run_id och ar last for alltid, aven i en aterppnad
   * vecka — annars kunde belopp som redan star pa en faktura andras i
   * efterhand. En stampling andrar aldrig radens version, sa en telefon som
   * legat offline far ALLTID det har beskedet och aldrig SYNC_CONFLICT.
   */
  ROW_INVOICED: {
    code: 'ROW_INVOICED',
    http_status: 409,
    retry: 'give_up',
    message_sv: 'Raden är redan med i ett fakturaunderlag och kan inte ändras. Ta bort fakturamärkningen först om den ska rättas.',
  },

  /**
   * TVA STALLEN, SAMMA INNEBORD: en rad kan inte tas bort sa lange nagot
   * fortfarande pekar pa den. I firmans DO ar det en kund med arbetsordrar
   * eller ett moment med tidrader; i D1 ar det DELETE_COMPANY mot en firma som
   * fortfarande betalar, med blocked_by 'subscription'.
   * TEXTEN NEDAN AR DEN ALLMANNA och visas nar klienten inte kanner igen
   * blocked_by. For 'subscription' visar skarmen i stallet en egen text som
   * sager att firman fortfarande betalar och att prenumerationen ska sagas upp
   * forst — samma sorts specialisering som ROW_DUPLICATE:s conflict finns for,
   * och den enda platsen dar en svensk text far skrivas ar app/src (se
   * DeleteCompanyRequest i shared/types.ts, dar samma regel star for
   * bekraftelsen).
   */
  ROW_IN_USE: {
    code: 'ROW_IN_USE',
    http_status: 409,
    retry: 'give_up',
    message_sv: 'Det här används på andra ställen och kan därför inte tas bort. Flytta eller ta bort det som använder det först.',
  },

  /**
   * Ett UNIQUE-index i schemat brast: tva franvarorader samma dag for samma
   * person, en andra prislista med is_default = 1, tva timpriser for samma
   * roll i samma lista, samma gast tilldelad samma order tva ganger, eller
   * en object_key som redan sitter pa en annan rad. Det ar INTE en
   * versionskonflikt — bada raderna ar nya och har olika id — och det ar
   * inte ett serverfel: tva telefoner (eller samma telefon tva ganger med
   * olika op_id) kan skapa lagets bada rader utan att nagot ar trasigt.
   * 'give_up' darfor att en omsandning av exakt samma operation aldrig kan
   * lyckas; anvandaren maste oppna raden i "Behover atgard" och andra den.
   */
  ROW_DUPLICATE: {
    code: 'ROW_DUPLICATE',
    http_status: 409,
    retry: 'give_up',
    message_sv: 'Det finns redan en sådan post. Öppna den som redan är sparad och ändra den i stället för att lägga till en ny.',
  },

  /**
   * Tva enheter har andrat samma rad; klienten visar serverns version mot
   * sin egen och later anvandaren valja. Samma fel nar en put med
   * base_version = null trafar ett id som redan finns, aven ett
   * mjukraderat — id ateranvands aldrig. (base_version = den mjukraderade
   * radens version ar daremot en aterupplivning och lyckas.) Samma fel nar
   * en 'invoice_run' bar ett id som redan finns: den operationen har ingen
   * base_version, sa uppslaget pa id ar dess enda skydd mot en andra
   * stampling.
   * DETAIL: { server_version, server_seq } — BADA null nar raden inte finns
   * alls (base_version ar ett tal mot ett okant id). Klienten visar da att
   * raden inte langre finns pa servern och later anvandaren skapa om eller
   * kasta; den far ALDRIG tolka 0 som en version.
   */
  SYNC_CONFLICT: {
    code: 'SYNC_CONFLICT',
    http_status: 409,
    retry: 'give_up',
    message_sv: 'Raden har ändrats på ett annat ställe sedan du började. Titta på ändringen och spara om.',
  },

  /**
   * Beslut 09: samma op_id fran samma konto och enhet med SAMMA innehall
   * ar ofarligt — da spelas svaret upp med status 'duplicate'. Det HAR
   * felet betyder samma op_id med ANNAT innehall, vilket alltid ar en bugg
   * eller en manipulerad utkorg. Ingenting andrades. (Ett op_id som
   * tillhor ett annat konto ger FORBIDDEN, aldrig ett uppspelat svar.)
   */
  DUPLICATE_OP: {
    code: 'DUPLICATE_OP',
    http_status: 409,
    retry: 'give_up',
    message_sv: 'Samma sparning skickades två gånger med olika innehåll. Ingenting ändrades. Gör om ändringen.',
  },

  /**
   * VAR DEN FYRAS: i ADD_MEMBER och SET_MEMBER_ROLE (se AddMemberRequest i
   * shared/types.ts) nar role ar 'guest' och guest_org_number saknas HELT.
   * SERVERN avvisar innan skrivningen, sa att beskedet pekar ut vad som
   * fattas; nar den nar databasen faller CHECK ((role = 'guest') = ...) i
   * schema/d1.sql anonymt och klienten kan inte saga vad som ska fyllas i.
   * Skild fran GUEST_ORG_NUMBER_INVALID, som triggrarna i schema/d1.sql
   * reser nar numret FINNS men pekar pa firman sjalv eller pa en firma
   * gasten sjalv ager.
   */
  GUEST_ORG_NUMBER_MISSING: {
    code: 'GUEST_ORG_NUMBER_MISSING',
    http_status: 400,
    retry: 'give_up',
    message_sv: 'En inhyrd måste läggas in med sitt eget företags organisationsnummer.',
  },

  GUEST_ORG_NUMBER_INVALID: {
    code: 'GUEST_ORG_NUMBER_INVALID',
    http_status: 400,
    retry: 'give_up',
    message_sv: 'Organisationsnumret måste tillhöra ett annat företag än ditt eget, och inte en firma personen själv äger här. Är personen anställd hos dig lägger du in honom som montör.',
  },

  /**
   * Arbetsordern finns inte, ar arkiverad — eller ar en order just den har
   * personen inte ser: en gast utan tilldelning far samma besked, sa att
   * svaret aldrig avslojar att den finns (beslut 17).
   */
  UNKNOWN_WORK_ORDER: {
    code: 'UNKNOWN_WORK_ORDER',
    http_status: 404,
    retry: 'give_up',
    message_sv: 'Arbetsordern finns inte längre. Välj en annan order för raden.',
  },

  UNKNOWN_REFERENCE: {
    code: 'UNKNOWN_REFERENCE',
    http_status: 404,
    retry: 'give_up',
    message_sv: 'Något du valde finns inte längre. Välj om och spara.',
  },

  /**
   * Beslut 15: priset fryses pa tidraden nar den sparas. Saknar den
   * traffade prislistan en rad for personens roll finns inget att frysa,
   * och raden far inte sparas prislos. Detaljen bar BARA rollen.
   */
  PRICE_MISSING: {
    code: 'PRICE_MISSING',
    http_status: 409,
    retry: 'give_up',
    message_sv: 'Det finns inget timpris för den rollen ännu. Chefen behöver fylla i prislistan innan tiden kan sparas.',
  },

  ORG_NUMBER_TAKEN: {
    code: 'ORG_NUMBER_TAKEN',
    http_status: 409,
    retry: 'give_up',
    message_sv: 'Det organisationsnumret används redan av en firma i Klartid. Be den som äger firman bjuda in dig.',
  },

  /**
   * TVA TAK, EN KOD: MAX_UPLOAD_BYTES for en bild (FILE_UPLOAD) och
   * MAX_VOICE_AUDIO_BYTES for ljudet i en rostrapport (VOICE_TRANSCRIBE).
   * Texten maste darfor vara sann for bada. detail.max_bytes sager vilket tak
   * som brast.
   */
  FILE_TOO_LARGE: {
    code: 'FILE_TOO_LARGE',
    http_status: 413,
    retry: 'give_up',
    message_sv: 'Det blev för stort att skicka. Välj en mindre bild, eller säg det kortare.',
  },

  TOO_MANY_OPS: {
    code: 'TOO_MANY_OPS',
    http_status: 413,
    retry: 'hold',
    message_sv: 'För mycket skickades på en gång. Appen delar upp det och försöker igen.',
  },

  RATE_LIMITED: {
    code: 'RATE_LIMITED',
    http_status: 429,
    retry: 'retry',
    message_sv: 'Det gick lite för fort. Appen försöker igen om en stund.',
  },

  SERVER_ERROR: {
    code: 'SERVER_ERROR',
    http_status: 500,
    retry: 'retry',
    message_sv: 'Något gick fel hos oss. Det du sparat ligger kvar i telefonen och skickas automatiskt.',
  },

  SERVICE_UNAVAILABLE: {
    code: 'SERVICE_UNAVAILABLE',
    http_status: 503,
    retry: 'retry',
    message_sv: 'Tjänsten svarar inte just nu. Det du sparat ligger kvar i telefonen och skickas automatiskt.',
  },
} as const satisfies Record<ErrorCode, ErrorSpec>;
